import { css } from '@emotion/core';
import { Collapse, Icon } from 'antd';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React from 'react';

import contentStyle from '@/src/cms/contentStyle';
import Accordion from '@/src/shared/components/Accordion';
import Sanitize from '@/src/shared/components/Sanitize';
import Table from '@/src/shared/components/Table';
import { bp } from '@/styles';

const { Panel } = Collapse;

const styles = {
  table: css`
    @media screen and (max-width: ${bp.sizes.sm}px) {
      td {
        &:nth-child(1) {
          min-width: 250px;
        }
        &:nth-child(2) {
          min-width: 450px;
        }
      }
    }
  `,
  header: css`
    @media ${bp.sm} {
      display: flex;
      justify-content: space-between;
    }
  `,
  link: css`
    font-size: 1.2rem;
    padding: 1.6rem 0 0;

    @media ${bp.sm} {
      padding: 0;
    }
  `,
};

const getValue = (record, camelKey, pascalKey) => {
  return record?.[camelKey] ?? record?.[pascalKey];
};

const columns = [
  {
    title: <b>Topic</b>,
    key: 'chartTopic',
    width: 250,
    render: record => getValue(record, 'chartTopic', 'ChartTopic'),
  },
  {
    title: <b>Details</b>,
    key: 'details',
    render: record => {
      const value = getValue(record, 'details', 'Details');
      return <Sanitize css={contentStyle}>{value}</Sanitize>;
    },
  },
];

const changeColumns = [
  {
    title: <b>Topic</b>,
    key: 'chartTopic',
    width: 250,
    render: record => getValue(record, 'chartTopic', 'ChartTopic'),
  },
  {
    title: <b>Recent Changes</b>,
    key: 'recentChanges',
    render: record => {
      const value = getValue(record, 'recentChanges', 'RecentChanges');
      return <Sanitize css={contentStyle}>{value}</Sanitize>;
    },
  },
];

const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

const chartCollapseProps = {
  ...filterCollapseProps,
  expandIconPosition: 'left',
};

const SearchResultsAccordion = ({ results = [], groupByChart, displayChanges }) => {
  return (
    <div>
      {groupByChart
        ? results.map((chart, index) => {
            const chartId = getValue(chart, 'chartId', 'ChartId');
            const chartTitle = getValue(chart, 'chartTitle', 'ChartTitle');
            const chartStates = chart.states || chart.States || [];

            return (
              <Accordion
                key={`${chartId || index}-compliassist-accordion`}
                id={(index + 1).toString()}
                panels={[
                  {
                    header: `${chartTitle}`,
                    content: {
                      type: 'generic',
                      items: [
                        <Collapse
                          {...chartCollapseProps}
                          key={`${chartId}`}
                          id={`${chartId}`}
                        >
                          {chartStates.map(chartState => {
                            const chartStateId = getValue(
                              chartState,
                              'chartStateId',
                              'ChartStateId'
                            );
                            const chartStateName = getValue(
                              chartState,
                              'chartState',
                              'ChartState'
                            );
                            const stateCode = getValue(chartState, 'stateCode', 'StateCode');
                            const topics = chartState.topics || chartState.Topics || [];

                            return (
                              <Panel
                                id="chart-result-header"
                                forceRender
                                header={
                                  <div css={styles.header}>
                                    <span>
                                      {chartStateName} ({topics.length} Results)
                                    </span>
                                    <div css={styles.link}>
                                      <Link
                                        href={{
                                          pathname: '/compliance/compliassist/summary',
                                          query: {
                                            chartId,
                                            stateCode,
                                          },
                                        }}
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <span id="chart-result-link">
                                          <Icon type="info-circle" theme="filled" />
                                          See Full Details
                                        </span>
                                      </Link>
                                    </div>
                                  </div>
                                }
                                key={`${chartStateId}`}
                              >
                                <div>
                                  <Table
                                    css={styles.table}
                                    rowKey={record => {
                                      const stateTopicId = getValue(
                                        record,
                                        'stateTopicId',
                                        'StateTopicId'
                                      );
                                      return `compliassist-state-topic-row-${stateTopicId}`;
                                    }}
                                    pagination={false}
                                    dataSource={topics}
                                    columns={displayChanges ? changeColumns : columns}
                                  />
                                </div>
                              </Panel>
                            );
                          })}
                        </Collapse>,
                      ],
                    },
                  },
                ]}
              />
            );
          })
        : results.map((state, index) => {
            const stateCode = getValue(state, 'stateCode', 'StateCode');
            const chartState = getValue(state, 'chartState', 'ChartState');
            const stateCharts = state.charts || state.Charts || [];

            return (
              <Accordion
                key={`${stateCode || index}-compliassist-accordion`}
                id={(index + 1).toString()}
                panels={[
                  {
                    header: `${chartState}`,
                    content: {
                      type: 'generic',
                      items: [
                        <Collapse
                          {...chartCollapseProps}
                          key={`${stateCode}`}
                          id={`${stateCode}`}
                        >
                          {stateCharts.map(stateChart => {
                            const chartId = getValue(stateChart, 'chartId', 'ChartId');
                            const chartTitle = getValue(
                              stateChart,
                              'chartTitle',
                              'ChartTitle'
                            );
                            const topics = stateChart.topics || stateChart.Topics || [];

                            return (
                              <Panel
                                forceRender
                                header={<span>{chartTitle}</span>}
                                key={`${chartId}`}
                              >
                                <div>
                                  <Table
                                    css={styles.table}
                                    rowKey={record => {
                                      const stateTopicId = getValue(
                                        record,
                                        'stateTopicId',
                                        'StateTopicId'
                                      );
                                      return `compliassist-state-topic-row-${stateTopicId}`;
                                    }}
                                    pagination={false}
                                    dataSource={topics}
                                    columns={displayChanges ? changeColumns : columns}
                                  />
                                </div>
                              </Panel>
                            );
                          })}
                        </Collapse>,
                      ],
                    },
                  },
                ]}
              />
            );
          })}
    </div>
  );
};

SearchResultsAccordion.propTypes = {
  results: PropTypes.array.isRequired,
};

export default SearchResultsAccordion;